import { useState, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, MicOff, Send, Trash2, Square } from "lucide-react";

interface VoiceRecorderProps {
  onSend: (blob: Blob) => void;
  onCancel: () => void;
}

export const VoiceRecorder = ({ onSend, onCancel }: VoiceRecorderProps) => {
  const [isRecording, setIsRecording] = useState(true);
  const [duration, setDuration] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : 'audio/webm',
      });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.start(100);
      setIsRecording(true);
      setDuration(0);

      timerRef.current = setInterval(() => {
        setDuration(prev => prev + 1);
      }, 1000);
    } catch (err) {
      console.error("Error starting recording:", err);
      onCancel();
    }
  }, [onCancel]);

  const stopAndSend = useCallback(() => {
    const mr = mediaRecorderRef.current;
    if (!mr || mr.state === "inactive") return;

    mr.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
      onSend(blob);
      cleanup();
    };
    mr.stop();
  }, [onSend]);

  const stopAndCancel = useCallback(() => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== "inactive") {
      mr.onstop = () => cleanup();
      mr.stop();
    } else {
      cleanup();
    }
    onCancel();
  }, [onCancel]);

  const cleanup = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
    }
    setIsRecording(false);
  };

  // Auto-start on mount
  useState(() => {
    startRecording();
  });

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
      className="flex items-center gap-2 px-3 py-2.5 border-t border-border/30 flex-shrink-0"
      style={{ backdropFilter: "blur(20px)", background: "hsl(var(--background) / 0.7)" }}
    >
      {/* Cancel */}
      <button
        onClick={stopAndCancel}
        className="p-2 rounded-full hover:bg-destructive/20 transition-colors"
      >
        <Trash2 className="w-5 h-5 text-destructive" />
      </button>

      {/* Recording indicator */}
      <div className="flex-1 flex items-center gap-3">
        <motion.div
          className="w-3 h-3 rounded-full bg-destructive"
          animate={{ opacity: [1, 0.3, 1] }}
          transition={{ duration: 1, repeat: Infinity }}
        />
        <div className="flex-1 h-8 rounded-full bg-muted/40 overflow-hidden flex items-center px-3">
          {/* Waveform animation */}
          <div className="flex items-center gap-[2px] flex-1">
            {Array.from({ length: 30 }).map((_, i) => (
              <motion.div
                key={i}
                className="w-[3px] rounded-full bg-primary/60"
                animate={{
                  height: isRecording
                    ? [4, Math.random() * 20 + 4, 4]
                    : 4,
                }}
                transition={{
                  duration: 0.4 + Math.random() * 0.3,
                  repeat: Infinity,
                  delay: i * 0.03,
                }}
              />
            ))}
          </div>
        </div>
        <span className="text-xs font-mono text-muted-foreground min-w-[36px]">
          {formatTime(duration)}
        </span>
      </div>

      {/* Send */}
      <button
        onClick={stopAndSend}
        className="p-2.5 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-all active:scale-90"
      >
        <Send className="w-4 h-4" />
      </button>
    </motion.div>
  );
};
